'use client';

import { useEffect, useRef, useState } from 'react';
import { 
  MessageSquareShare, Bot, Store, DollarSign, Megaphone, 
  Kanban, Waypoints, MapPin, ClipboardList, Wrench, 
  Briefcase, MessagesSquare, CalendarDays, Users, 
  Contact, BellRing, Database, PieChart
} from 'lucide-react';
import styles from './BentoEcosystem.module.css';

// Módulos com shapes definidos para montar o "Quebra-Cabeça Tetris"
const modules = [
  { id: 'chat', title: 'Chat Omnichannel', desc: 'Distribuição automática e SLA em uma tela.', icon: MessageSquareShare, color: '#2563eb', bg: '#dbeafe', shape: styles.block2x2 },
  { id: 'vendas', title: 'Vendas & PDV', desc: 'Frente de caixa rápida e multi-lojas.', icon: Store, color: '#ea580c', bg: '#ffedd5', shape: styles.rect2x1 },
  { id: 'financas', title: 'Financeiro', desc: 'NFe, boletos e DRE.', icon: DollarSign, color: '#166534', bg: '#dcfce7', shape: styles.rect1x2 },
  { id: 'ai', title: 'Agentes de IA', desc: 'Robôs que vendem 24/7.', icon: Bot, color: '#8b5cf6', bg: '#ede9fe', shape: styles.square1x1 },
  { id: 'campanhas', title: 'Campanhas', desc: 'WhatsApp, SMS e E-mail.', icon: Megaphone, color: '#ef4444', bg: '#fee2e2', shape: styles.square1x1 },
  { id: 'crm', title: 'CRM Kanban', desc: 'Arraste cards e feche negócios.', icon: Kanban, color: '#0284c7', bg: '#e0f2fe', shape: styles.block2x2 },
  { id: 'rh', title: 'Gestão de RH', desc: '', icon: Briefcase, color: '#c026d3', bg: '#fae8ff', shape: styles.square1x1 },
  { id: 'fluxos', title: 'Flow Builder', desc: '', icon: Waypoints, color: '#0d9488', bg: '#ccfbf1', shape: styles.square1x1 },
  { id: 'maps', title: 'Buscador Maps', desc: '', icon: MapPin, color: '#ea580c', bg: '#ffedd5', shape: styles.square1x1 },
  { id: 'forms', title: 'Forms', desc: '', icon: ClipboardList, color: '#4f46e5', bg: '#e0e7ff', shape: styles.square1x1 },
  { id: 'os', title: 'Ordem de Serviço', desc: 'Gestão de técnicos em campo.', icon: Wrench, color: '#b45309', bg: '#fef3c7', shape: styles.rect2x1 },
  { id: 'chatint', title: 'Chat Interno', desc: '', icon: MessagesSquare, color: '#2563eb', bg: '#dbeafe', shape: styles.square1x1 },
  { id: 'agenda', title: 'Agenda Web', desc: '', icon: CalendarDays, color: '#059669', bg: '#d1fae5', shape: styles.square1x1 },
  { id: 'wppgroups', title: 'Grupos WA', desc: '', icon: Users, color: '#16a34a', bg: '#dcfce7', shape: styles.square1x1 },
  { id: 'clientes', title: 'Clientes (B2B)', desc: '', icon: Contact, color: '#0284c7', bg: '#e0f2fe', shape: styles.square1x1 },
  { id: 'followup', title: 'Follow-ups', desc: '', icon: BellRing, color: '#ea580c', bg: '#ffedd5', shape: styles.square1x1 },
  { id: 'noc', title: 'NOC & Backup', desc: '', icon: Database, color: '#475569', bg: '#f1f5f9', shape: styles.square1x1 },
  { id: 'bi', title: 'Business Intelligence', desc: 'Cruze dados de todos os módulos.', icon: PieChart, color: '#db2777', bg: '#fce7f3', shape: styles.rect3x1 },
];

export function BentoEcosystem() {
  const [isVisible, setIsVisible] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (gridRef.current) observer.unobserve(gridRef.current);
        }
      },
      { threshold: 0.15 } // Dispara quando 15% do bloco entra na tela
    );

    if (gridRef.current) observer.observe(gridRef.current);
    return () => {
      if (gridRef.current) observer.unobserve(gridRef.current);
    };
  }, []);

  return (
    <section className={styles.bentoSection} id="modulos">
      
      {/* TEXTO PODEROSO */}
      <div className={styles.bentoHeader}>
        <span className={styles.badge}>Arquitetura Modular</span>
        <h2 className={styles.title}>
          O Aesir é <span className={styles.titleHighlight}>infinitamente mais poderoso</span> com o ecossistema completo.
        </h2>
        <p className={styles.subtitle}>
          18 peças projetadas para se encaixarem perfeitamente. Cada módulo potencializa o outro, 
          criando uma máquina de vendas e gestão impossível de ser parada.
        </p>
      </div>

      {/* O TABULEIRO TETRIS */}
      <div ref={gridRef} className={`${styles.bentoGrid} ${isVisible ? styles.inView : ''}`}>
        
        {modules.map((mod, index) => {
          const IconComponent = mod.icon;
          
          // Efeito Cascata Orgânico: As peças não caem todas de uma vez, 
          // criei um randomizador leve misturado com o index para dar a sensação 
          // de que estão despencando e se encaixando.
          const cascadeDelay = (index * 0.04) + (Math.random() * 0.1);

          return (
            <div 
              key={mod.id} 
              className={`${styles.bentoCard} ${mod.shape}`}
              style={isVisible ? { animationDelay: `${cascadeDelay}s` } : {}}
            >
              <div className={styles.cardIcon} style={{ background: mod.bg, color: mod.color }}>
                <IconComponent size={22} strokeWidth={2.5} />
              </div>
              
              <div>
                <h3 className={styles.cardTitle}>{mod.title}</h3>
                {mod.desc && <p className={styles.cardDesc}>{mod.desc}</p>}
              </div>
            </div>
          );
        })}

      </div>
    </section>
  );
}